import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { TextField, MenuItem, Tooltip, Button } from "@mui/material";
import { toast } from "react-toastify";
import moment from "moment";
import useAxios from "../../helpers/Hooks/useAxios";
import { URLS } from "../../helpers/URLS";
import SkeletonLoader from "../../loaders/Skeleton";
import NoData from "../../loaders/NoData";
import PaginationGlobal from "../TablePagination.js/PaginationGlobal";
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import VisibilityRounded from "@mui/icons-material/VisibilityRounded";
import EditRounded from "@mui/icons-material/EditRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import "./HelpDesk.scss";

const CATEGORIES = ["Hardware", "Software", "Network", "Access", "Email", "Other"];
const PRIORITIES = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
];

const STATUS_LABEL = {
    open: "Open",
    in_progress: "In progress",
    resolved: "Resolved",
    closed: "Closed",
};

const emptyTicket = { subject: "", description: "", category: "Hardware", priority: "medium" };

const HelpDesk = () => {
    const { fetchData } = useAxios();

    const [tab, setTab] = useState("mine");
    const [isAdmin, setIsAdmin] = useState(false);
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageNumber, setPageNumber] = useState(1);

    const [raiseOpen, setRaiseOpen] = useState(false);
    const [form, setForm] = useState({ ...emptyTicket });
    const [saving, setSaving] = useState(false);

    const [active, setActive] = useState(null);
    const [resolution, setResolution] = useState("");
    const [editing, setEditing] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [myId, setMyId] = useState(null);

    const countPerPage = 8;

    useEffect(() => {
        checkAccess();
    }, []);

    useEffect(() => {
        loadTickets();
        setPageNumber(1);
    }, [tab]);

    const checkAccess = async () => {
        const resp = await fetchData({ url: URLS.helpDeskAccess, method: "get" });
        if (resp?.status === 200) {
            setIsAdmin(Boolean(resp.data?.isAdmin));
            setMyId(resp.data?.employeeId || null);
        }
    };

    const loadTickets = async () => {
        setLoading(true);
        const url = tab === "queue" ? URLS.allTickets : URLS.myTickets;
        const resp = await fetchData({ url, method: "get" });
        if (resp?.status === 200) setTickets(resp.data || []);
        else setTickets([]);
        setLoading(false);
    };

    const submitTicket = async () => {
        if (!form.subject.trim()) {
            toast.error("Please add a subject");
            return;
        }
        setSaving(true);

        const resp = editing
            ? await fetchData({
                url: URLS.updateTicket, method: "put",
                body: { ...form, ticketId: editing.ticketId },
            })
            : await fetchData({ url: URLS.createTicket, method: "post", body: form });

        setSaving(false);

        if (resp?.status === 200) {
            toast.success(resp.message || (editing ? "Ticket updated" : "Ticket raised"));
            closeForm();
            loadTickets();
        } else {
            toast.error(resp?.message || "Could not save the ticket");
        }
    };

    const openForm = (ticket) => {
        if (ticket) {
            setEditing(ticket);
            setForm({
                subject: ticket.subject || "",
                description: ticket.description || "",
                category: ticket.category || "Hardware",
                priority: ticket.priority || "medium",
            });
        } else {
            setEditing(null);
            setForm({ ...emptyTicket });
        }
        setRaiseOpen(true);
    };

    const closeForm = () => {
        setRaiseOpen(false);
        setEditing(null);
        setForm({ ...emptyTicket });
    };

    const removeTicket = async () => {
        const resp = await fetchData({
            url: `${URLS.deleteTicket}?ticketId=${encodeURIComponent(confirmDelete.ticketId)}`,
            method: "delete",
        });

        if (resp?.status === 200) {
            toast.success(resp.message || "Ticket removed");
            setConfirmDelete(null);
            loadTickets();
        } else {
            toast.error(resp?.message || "Could not remove the ticket");
        }
    };

    /* a requester may still change their own ticket while nobody has picked it up */
    const canManage = (t) => isAdmin || (t.status === "open" && (!myId || t.raisedBy === myId));

    const setStatus = async (status) => {
        const resp = await fetchData({
            url: URLS.updateTicketStatus,
            method: "put",
            body: { ticketId: active.ticketId, status, resolution },
        });

        if (resp?.status === 200) {
            toast.success(resp.message || "Ticket updated");
            setActive(null);
            loadTickets();
        } else {
            toast.error(resp?.message || "Could not update the ticket");
        }
    };

    const openTicket = (ticket) => {
        setActive(ticket);
        setResolution(ticket.resolution || "");
    };

    const page = tickets.slice((pageNumber - 1) * countPerPage, pageNumber * countPerPage);

    return (
        <div className="help-desk">
            <div className="hd-head d-flex align-items-center">
                <h4 className="title">Help Desk</h4>
                <div className="ms-auto">
                    <Button variant="contained" className="hd-raise-btn" onClick={() => openForm(null)}>
                        <AddCircleRounded /> Raise a ticket
                    </Button>
                </div>
            </div>

            {isAdmin && (
                <div className="hd-tabs" role="tablist">
                    <button
                        type="button"
                        className={`hd-tab${tab === "mine" ? " is-on" : ""}`}
                        onClick={() => setTab("mine")}
                    >
                        My Tickets
                    </button>
                    <button
                        type="button"
                        className={`hd-tab${tab === "queue" ? " is-on" : ""}`}
                        onClick={() => setTab("queue")}
                    >
                        Support Queue
                    </button>
                </div>
            )}

            <div className="bg-wht">
                {loading ? <SkeletonLoader /> : tickets.length > 0 ? (
                    <>
                        <div className="table-responsive tab-styles">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th scope="col">Ticket</th>
                                        {tab === "queue" && <th scope="col">Raised By</th>}
                                        <th scope="col">Subject</th>
                                        <th scope="col">Category</th>
                                        <th scope="col">Priority</th>
                                        <th scope="col">Raised On</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {page.map((t) => (
                                        <tr key={t.ticketId}>
                                            <td className="hd-id">{t.ticketId}</td>
                                            {tab === "queue" && <td className="hd-who">{t.employeeName}</td>}
                                            <td className="hd-subject">{t.subject}</td>
                                            <td>{t.category}</td>
                                            <td>
                                                <span className={`hd-priority is-${t.priority}`}>{t.priority}</span>
                                            </td>
                                            <td className="hd-date">{moment(t.createdDate).format("DD-MM-YYYY")}</td>
                                            <td>
                                                <span className={`hd-status is-${t.status}`}>
                                                    {STATUS_LABEL[t.status] || t.status}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="hd-actions">
                                                    <Tooltip title="View ticket" placement="top" arrow>
                                                        <span>
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary p-0"
                                                                onClick={() => openTicket(t)}
                                                            >
                                                                <VisibilityRounded />
                                                            </button>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={canManage(t) ? "Edit ticket" : "Only open tickets can be edited"} placement="top" arrow>
                                                        <span>
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary p-0"
                                                                disabled={!canManage(t)}
                                                                onClick={() => openForm(t)}
                                                            >
                                                                <EditRounded />
                                                            </button>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={canManage(t) ? "Delete ticket" : "Only open tickets can be withdrawn"} placement="top" arrow>
                                                        <span>
                                                            <button
                                                                type="button"
                                                                className="btn btn-danger p-0"
                                                                disabled={!canManage(t)}
                                                                onClick={() => setConfirmDelete(t)}
                                                            >
                                                                <DeleteOutlineRounded />
                                                            </button>
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {tickets.length > countPerPage && (
                            <PaginationGlobal
                                count={Math.ceil(tickets.length / countPerPage)}
                                pageNumber={pageNumber}
                                func={setPageNumber}
                            />
                        )}
                    </>
                ) : <NoData />}
            </div>

            {/* ---------- raise ---------- */}
            <Modal show={raiseOpen} onHide={closeForm} className="hd-modal" size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editing ? `Edit ${editing.ticketId}` : "Raise a ticket"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="hd-hint">
                        Tell us what is wrong and the desk will pick it up. You will get an email when it is resolved.
                    </p>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                select fullWidth size="small" label="Category" sx={{ mt: 2 }}
                                value={form.category}
                                onChange={(e) => setForm({ ...form, category: e.target.value })}
                            >
                                {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </TextField>
                        </div>
                        <div className="col-md-6">
                            <TextField
                                select fullWidth size="small" label="Priority" sx={{ mt: 2 }}
                                value={form.priority}
                                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                            >
                                {PRIORITIES.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                            </TextField>
                        </div>
                        <div className="col-md-12">
                            <TextField
                                fullWidth size="small" label="Subject" sx={{ mt: 2 }}
                                placeholder="Laptop will not boot"
                                value={form.subject}
                                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                            />
                        </div>
                        <div className="col-md-12">
                            <TextField
                                fullWidth multiline rows={4} label="What is happening?" sx={{ mt: 2 }}
                                placeholder="When it started, what you have already tried, any error message."
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                            />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="contained" color="inherit" onClick={closeForm}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={submitTicket} disabled={saving}>
                        {saving ? "Saving…" : editing ? "Save changes" : "Submit ticket"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ---------- detail ---------- */}
            <Modal show={Boolean(active)} onHide={() => setActive(null)} className="hd-modal" size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{active?.ticketId} · {active?.subject}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {active && (
                        <>
                            <dl className="hd-facts">
                                <div><dt>Status</dt><dd><span className={`hd-status is-${active.status}`}>{STATUS_LABEL[active.status]}</span></dd></div>
                                <div><dt>Priority</dt><dd><span className={`hd-priority is-${active.priority}`}>{active.priority}</span></dd></div>
                                <div><dt>Category</dt><dd>{active.category}</dd></div>
                                <div><dt>Raised by</dt><dd>{active.employeeName}</dd></div>
                                <div><dt>Raised on</dt><dd>{moment(active.createdDate).format("DD MMM YYYY, h:mm A")}</dd></div>
                                {active.resolvedAt && (
                                    <div><dt>Closed on</dt><dd>{moment(active.resolvedAt).format("DD MMM YYYY, h:mm A")}</dd></div>
                                )}
                            </dl>

                            {active.description && (
                                <div className="hd-block">
                                    <span className="hd-block-label">Description</span>
                                    <p>{active.description}</p>
                                </div>
                            )}

                            {isAdmin ? (
                                <TextField
                                    fullWidth multiline rows={3} label="Resolution notes" sx={{ mt: 2 }}
                                    placeholder="What you did to fix it — this is sent to the requester."
                                    value={resolution}
                                    onChange={(e) => setResolution(e.target.value)}
                                />
                            ) : active.resolution ? (
                                <div className="hd-block hd-block--resolution">
                                    <span className="hd-block-label">Resolution</span>
                                    <p>{active.resolution}</p>
                                </div>
                            ) : null}
                        </>
                    )}
                </Modal.Body>
                {isAdmin && active && active.status !== "closed" && (
                    <Modal.Footer>
                        {active.status === "open" && (
                            <Button variant="contained" color="inherit" onClick={() => setStatus("in_progress")}>
                                Start working
                            </Button>
                        )}
                        <Button variant="contained" onClick={() => setStatus("resolved")}>
                            Mark resolved
                        </Button>
                    </Modal.Footer>
                )}
            </Modal>
            {/* ---------- confirm delete ---------- */}
            <Modal show={Boolean(confirmDelete)} onHide={() => setConfirmDelete(null)} className="hd-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Remove {confirmDelete?.ticketId}?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="hd-hint">
                        <strong>{confirmDelete?.subject}</strong> will be taken off the list. This cannot be undone
                        from here.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="contained" color="inherit" onClick={() => setConfirmDelete(null)}>
                        Keep it
                    </Button>
                    <Button variant="contained" color="error" onClick={removeTicket}>
                        Remove ticket
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default HelpDesk;
